import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import ResetConfigDropdown from './ResetConfigDropdown';
import { useQuotes } from '../context/QuotesContext';
import { useConfiguratorCompactLayout } from '../hooks/useMediaQuery';
import { buildConfiguratorNavigationState } from '../lib/contactConfigurator';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';

const ConfiguratorShell = ({
  title,
  drawingBoardRef,
  onReset,
  optionsPanel,
  previewPanel,
  summaryPanel,
  optionsAsideClassName = '',
  previewCentered = false,
  isDirty = false,
  onMarkClean,
  saveCurrentRef,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { startNewQuoteSession } = useQuotes();
  const isCompactLayout = useConfiguratorCompactLayout();
  const [showSummary, setShowSummary] = useState(true);
  const [tabletTab, setTabletTab] = useState('preview');
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const pendingNavigationRef = useRef(null);

  const tabletTabs = [
    { id: 'options', label: t('configurator.tabOptions') },
    { id: 'preview', label: t('configurator.tabPreview') },
    { id: 'summary', label: t('configurator.tabSummary') },
  ];

  const summaryWithCompact =
    React.isValidElement(summaryPanel)
      ? React.cloneElement(summaryPanel, { compact: isCompactLayout })
      : summaryPanel;

  const previewWrapperClass = previewCentered
    ? 'h-full w-full min-h-[240px] flex items-center justify-center'
    : 'w-full h-full min-h-[240px]';

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const attemptNavigation = (navFn) => {
    if (!isDirty) {
      navFn();
      return;
    }
    pendingNavigationRef.current = navFn;
    setLeaveDialogOpen(true);
  };

  const closeLeaveDialog = () => {
    setLeaveDialogOpen(false);
    pendingNavigationRef.current = null;
  };

  const completePendingNavigation = () => {
    const navFn = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setLeaveDialogOpen(false);
    navFn?.();
  };

  const handleLeaveSave = async () => {
    if (!saveCurrentRef?.current) {
      if (isDirty) {
        console.error('Save handler unavailable — current drawing was not saved');
        return;
      }
      completePendingNavigation();
      return;
    }
    setLeaveSaving(true);
    try {
      await saveCurrentRef.current();
      onMarkClean?.();
      completePendingNavigation();
    } catch (err) {
      console.error('Failed to save before leaving configurator', err);
    } finally {
      setLeaveSaving(false);
    }
  };

  const handleLeaveDiscard = () => {
    onMarkClean?.();
    completePendingNavigation();
  };

  const handleNewOffers = () => {
    attemptNavigation(() => {
      if (window.confirm(t('configurator.newOffersConfirm'))) {
        startNewQuoteSession();
        onMarkClean?.();
        navigate('/configurator-select', {
          replace: true,
          state: buildConfiguratorNavigationState(
            location.state,
            { newQuote: true },
            user?.id
          ),
        });
      }
    });
  };

  const renderPreview = (ref) => (
    <div className="h-full min-h-0 p-3 sm:p-4 xl:p-6 overflow-hidden relative">
      <div ref={ref} className={previewWrapperClass}>
        {previewPanel}
      </div>
    </div>
  );

  return (
    <>
      <div className="configurator-shell configurator-safe-top w-full overflow-hidden bg-gray-50 flex flex-col">
        <div className="bg-slate-800 text-white px-3 py-2.5 sm:px-4 sm:py-3 xl:px-6 xl:py-4 shadow-lg shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  attemptNavigation(() => {
                    navigate('/configurator-select', {
                      state: buildConfiguratorNavigationState(
                        location.state,
                        { preserveQuoteSession: true },
                        user?.id
                      ),
                    });
                  });
                }}
                className="inline-flex shrink-0 items-center px-2.5 py-1.5 sm:px-3 rounded-md border border-slate-600 text-xs sm:text-sm text-white hover:bg-slate-700 transition-colors touch-manipulation"
              >
                <span className="mr-1">←</span>
                <span className="hidden sm:inline">{t('configurator.back')}</span>
              </button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg xl:text-2xl font-bold truncate">{title}</h1>
                <p className="hidden xl:block text-sm text-slate-300 mt-0.5">
                  {t('configurator.professionalSalesTool')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-4 shrink-0">
              <button
                type="button"
                onClick={handleNewOffers}
                className="px-2.5 py-1.5 sm:px-4 sm:py-2 border border-slate-500 hover:bg-slate-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap"
              >
                {t('configurator.newOffers')}
              </button>
              <button
                type="button"
                onClick={() =>
                  attemptNavigation(() =>
                    navigate('/export-quote', {
                      state: buildConfiguratorNavigationState(
                        location.state,
                        { from: location.pathname, preserveQuoteSession: true },
                        user?.id
                      ),
                    })
                  )
                }
                className="px-2.5 py-1.5 sm:px-4 sm:py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation whitespace-nowrap"
              >
                {t('configurator.exportQuote')}
              </button>
              <ResetConfigDropdown onReset={onReset} />
              {isAuthenticated ? (
                <button
                  type="button"
                  onClick={() =>
                    attemptNavigation(() => navigate('/dashboard?tab=quotes'))
                  }
                  className="hidden sm:inline-flex px-3 py-1.5 sm:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation"
                >
                  My Quotes
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex flex-1 min-h-0 flex-col">
          {isCompactLayout ? (
            <>
              <div className="flex-1 min-h-0 overflow-hidden">
                {tabletTab === 'options' ? (
                  <div className={`h-full min-h-0 ${optionsAsideClassName}`}>{optionsPanel}</div>
                ) : null}
                {tabletTab === 'preview' ? renderPreview(drawingBoardRef) : null}
                <div
                  className={`h-full min-h-0 overflow-y-auto p-2 sm:p-3 ${
                    tabletTab === 'summary' ? '' : 'hidden'
                  }`}
                >
                  {summaryWithCompact}
                </div>
              </div>
              <nav
                className="shrink-0 grid grid-cols-3 gap-1 border-t border-gray-200 bg-white px-2 py-2 configurator-safe-bottom shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
                aria-label={title}
              >
                {tabletTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setTabletTab(tab.id)}
                    className={`min-h-[48px] rounded-lg px-2 py-2 text-xs sm:text-sm font-semibold transition-colors touch-manipulation ${
                      tabletTab === tab.id
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </>
          ) : (
            <div className="flex flex-1 min-h-0">
              <div
                className={`w-[min(100%,380px)] xl:w-1/4 shrink-0 overflow-hidden border-r border-gray-200 ${optionsAsideClassName}`}
              >
                {optionsPanel}
              </div>

              <div className="flex-1 min-w-0 overflow-hidden relative">
                {renderPreview(drawingBoardRef)}
                <button
                  type="button"
                  onClick={() => setShowSummary(!showSummary)}
                  className="absolute right-4 bottom-4 z-50 bg-orange-500 hover:bg-orange-600 text-white rounded-l-lg px-3 py-4 shadow-lg transition-all duration-300 flex items-center justify-center touch-manipulation"
                  title={showSummary ? 'Hide Summary' : 'Show Summary'}
                >
                  <svg
                    className={`w-5 h-5 transition-transform duration-300 ${showSummary ? 'rotate-0' : 'rotate-180'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div
                className={`relative overflow-hidden border-l bg-white transition-all duration-300 ease-in-out shrink-0 ${
                  showSummary ? 'w-[min(100%,520px)] xl:w-[560px]' : 'w-0 border-0'
                }`}
              >
                <div
                  className={`h-full p-3 xl:p-4 transition-opacity duration-300 ${
                    showSummary ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {summaryWithCompact}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={leaveDialogOpen} onOpenChange={(open) => !open && closeLeaveDialog()}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('configurator.unsavedTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('configurator.unsavedDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col sm:space-x-0">
            <div className="flex w-full flex-col gap-2 sm:flex-row">
              <AlertDialogCancel disabled={leaveSaving} className="mt-0 flex-1">
                {t('configurator.unsavedCancel')}
              </AlertDialogCancel>
              <Button
                type="button"
                variant="outline"
                disabled={leaveSaving}
                onClick={handleLeaveDiscard}
                className="flex-1"
              >
                {t('configurator.unsavedDiscard')}
              </Button>
            </div>
            <Button
              type="button"
              disabled={leaveSaving}
              onClick={handleLeaveSave}
              className="mt-0 w-full"
            >
              {leaveSaving ? t('configurator.unsavedSaving') : t('configurator.unsavedSave')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ConfiguratorShell;
